import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCard, useHelp } from 'context/HelpContext';
import {
  getRouteKind,
  providerHasPushAction,
} from 'components/common/routeKind';
import { Provider } from 'components/common/ObjectTypes';

// The Help side panel for the Route page: which cards it shows, and how it
// makes room for the Rules side panel when both are open.
export const useRouteHelp = ({
  providerType,
  subpage,
  rulesPanelOpen,
}: {
  providerType: Provider['type'];
  subpage: string;
  rulesPanelOpen: boolean;
}) => {
  const { t, i18n } = useTranslation();
  const {
    setHelpContent,
    closePanel: closeHelpPanel,
    setRightOffsetPx: setHelpOffsetPx,
  } = useHelp();

  useEffect(() => {
    // The Route-rules guide is tied to the Rules panel, not the provider type, so
    // it's built independently of the provider-type guard below.
    const rulesCards: HelpCard[] = rulesPanelOpen
      ? [
          {
            title: t('Route Rules'),
            description: t(
              'Learn how to transform and map data with Route rules.',
            ),
            button: {
              label: t('Learn More'),
              url: 'https://support.earthranger.com/routes-gundi/route-rules',
            },
          },
        ]
      : [];

    if (!providerType?.id || !providerType?.value) {
      setHelpContent(rulesCards.length > 0 ? { cards: rulesCards } : null);
      return;
    }

    const kind = getRouteKind(providerType);
    const cards: HelpCard[] = [
      // While the Rules side panel is open, surface the Route-rules guide first.
      ...rulesCards,
      // The flow map is the page's main content and a new idea, so it gets a
      // card of its own wherever it is on screen.
      ...(subpage === 'configurations'
        ? [
            {
              title: t('Configuring a Route'),
              description: t(
                'See how data moves between the systems in this Route, and set up each direction.',
              ),
              button: {
                label: t('Learn More'),
                url: 'https://support.earthranger.com/routes-gundi/routes',
              },
            },
            {
              title: t('Add a Destination to a Route'),
              description: t(
                'Learn the three ways to add a destination to a Route.',
              ),
              button: {
                label: t('Learn More'),
                url: 'https://support.earthranger.com/routes-gundi/add-destination-to-route',
              },
            },
          ]
        : []),
      ...(kind === 'webhook'
        ? [
            {
              title: t('Gundi Webhook API'),
              description: t('Learn how to send data to Gundi using webhooks.'),
              button: {
                label: t('Learn More'),
                url: 'https://support.earthranger.com/developer_docs/custom-webhook',
              },
            },
          ]
        : []),
      ...(kind === 'push-only' || providerHasPushAction(providerType)
        ? [
            {
              title: t('Gundi API'),
              description: t('Learn how to push data to Gundi using the API.'),
              button: {
                label: t('Learn More'),
                url: 'https://support.earthranger.com/developer_docs/gundi-api',
              },
            },
          ]
        : []),
    ];
    setHelpContent(cards.length > 0 ? { cards } : null);
  }, [providerType, rulesPanelOpen, subpage, i18n.language, t, setHelpContent]);

  useEffect(() => {
    return () => setHelpContent(null);
  }, [setHelpContent]);

  // Coordinate the Help side panel with the Rules side panel across three width ranges:
  //   >= 1400px : both coexist side-by-side — shift Help + "?" left by the Rules width.
  //   768-1400  : not enough room for both, so close Help when Rules opens (see effect below).
  //   < 768px   : the md/hamburger breakpoint — Rules is full-screen and Help stacks
  //               full-screen on top of it, so there's nothing to shift (offset stays 0).
  // The offset is recomputed on resize so dragging across a breakpoint stays consistent.
  useEffect(() => {
    const RULES_PANEL_WIDTH = 520;
    const FULLSCREEN_MAX_WIDTH = 768;
    const applyOffset = () => {
      if (!rulesPanelOpen) {
        setHelpOffsetPx(0);
        return;
      }
      const width =
        typeof window !== 'undefined' ? window.innerWidth : RULES_PANEL_WIDTH;
      setHelpOffsetPx(width < FULLSCREEN_MAX_WIDTH ? 0 : RULES_PANEL_WIDTH);
    };
    applyOffset();
    window.addEventListener('resize', applyOffset);
    return () => {
      window.removeEventListener('resize', applyOffset);
      setHelpOffsetPx(0);
    };
  }, [rulesPanelOpen, setHelpOffsetPx]);

  // One-shot when Rules opens: in the mid range Help can't sit beside it, so close Help and
  // let the user re-open it from the (shifted) "?". Below the full-screen breakpoint they
  // stack instead, so Help is left alone there.
  useEffect(() => {
    if (!rulesPanelOpen) return;
    const COEXIST_MIN_WIDTH = 1400;
    const FULLSCREEN_MAX_WIDTH = 768;
    const width =
      typeof window !== 'undefined' ? window.innerWidth : COEXIST_MIN_WIDTH;
    if (width >= FULLSCREEN_MAX_WIDTH && width < COEXIST_MIN_WIDTH) {
      closeHelpPanel();
    }
  }, [rulesPanelOpen, closeHelpPanel]);
};
